import React from "react";
import { Link } from "@mui/material";

/** Detects http(s) URLs and www.… segments in plain text. */
const URL_IN_TEXT = /(https?:\/\/[^\s<>]+|www\.[^\s<>]+)/gi;

function stripTrailingPunctuation(s: string): string {
  return s.replace(/[.,;:!?)\]]+$/, "");
}

/** Only allow navigable http(s) URLs (no javascript:, data:, etc.). */
function safeHref(raw: string): string | null {
  const candidate = stripTrailingPunctuation(raw.trim());
  if (!candidate) return null;

  if (/^https?:\/\//i.test(candidate)) {
    try {
      const url = new URL(candidate);
      if (url.protocol !== "http:" && url.protocol !== "https:") return null;
      return url.href;
    } catch {
      return null;
    }
  }

  if (/^www\./i.test(candidate)) {
    return `https://${candidate}`;
  }

  return null;
}

/**
 * Renders plain text with URLs turned into external links.
 * Safe for untrusted description text: only http/https hrefs are produced.
 */
export function renderTextWithLinks(text: string): React.ReactNode {
  if (!text) return null;

  const parts = text.split(URL_IN_TEXT);

  return parts.map((part, index) => {
    const href = safeHref(part);
    if (href) {
      return (
        <Link
          key={index}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ wordBreak: "break-all" }}
        >
          {part}
        </Link>
      );
    }
    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
}
